import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAuth } from './AuthBridge';
import { SponsoredService } from '../services/sponsoredService';
import { AdConfig, AdPlacementConfig } from '../types';
import { 
  ShieldAlert, ShieldCheck, ExternalLink, Sparkles, Megaphone,
  Image as ImageIcon, ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router';
import { PageLoader } from './PageLoader';

interface AdUnitProps {
  placement: keyof AdConfig['placements'] | 'study_guide' | 'skills_lab' | string;
  refreshKey?: number;
  className?: string;
}

/**
 * Universal Responsive AdUnit Component
 * Renders Network (Google AdSense / custom tags) and Internal bespoke advertisements
 * with mathematical auto-scaling for mobile screens to eliminate truncation, overflow,
 * and clipping across all device sizes (320px to 4K+).
 */
export const AdUnit: React.FC<AdUnitProps> = ({ placement, refreshKey = 0, className = '' }) => {
  const { user, activeCert } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState<AdConfig>(SponsoredService.getConfig());
  const [isReady, setIsReady] = useState(false);
  const htmlContainerRef = useRef<HTMLDivElement>(null);
  const scalingWrapperRef = useRef<HTMLDivElement>(null);
  const isPushedRef = useRef(false);
  const uniqueId = useMemo(() => `ads-${placement.replace(/[^a-zA-Z0-9_-]/g, '_')}-${refreshKey}-${Math.random().toString(36).substr(2, 7)}`, [placement, refreshKey]);

  const isAdmin = user?.role === 'admin';
  const normalizedPlacement = (
    placement === 'study_guide' ? 'study_guide_top' : 
    placement === 'skills_lab' ? 'skills_lab_top' : 
    placement
  ) as keyof AdConfig['placements'];

  const placementConfig: AdPlacementConfig = config.placements?.[normalizedPlacement] || {
    enabled: true,
    source: 'internal',
    htmlSnippet: ''
  };

  const isNetworkSource = placementConfig?.source === 'network';

  useEffect(() => {
    const handleUpdate = () => setConfig(SponsoredService.getConfig());
    window.addEventListener('sponsored-config-updated', handleUpdate);
    
    SponsoredService.fetchLatestConfig().then(latest => {
        setConfig(latest);
        setIsReady(true);
    });
    
    return () => window.removeEventListener('sponsored-config-updated', handleUpdate);
  }, []);

  /**
   * Intelligently inspects DOM and styles to calculate real unscaled dimensions
   * of embedded ads (fixed size 728x90, 300x250, 336x280, 468x60, 970x250, 160x600, 320x50, etc.)
   * and scales them to fit container width without squishing or truncating.
   */
  const executeSafeScale = useCallback(() => {
    if (!htmlContainerRef.current) return;
    const container = htmlContainerRef.current;
    const wrapper = scalingWrapperRef.current;
    const parentWidth = container.clientWidth || container.offsetWidth;
    if (parentWidth <= 0) return;

    // Check if the ad contains responsive AdSense tags (fluid auto-format)
    const insElements = Array.from(container.querySelectorAll('ins.adsbygoogle')) as HTMLElement[];
    const isExplicitlyResponsive = insElements.some(ins => {
      const format = ins.getAttribute('data-ad-format');
      const isFullWidth = ins.getAttribute('data-full-width-responsive');
      return (format === 'auto' || format === 'fluid' || isFullWidth === 'true') && !ins.style.width?.includes('px');
    });

    // Detect target unscaled width & height from children or attributes
    let targetWidth = 0;
    let targetHeight = 0;

    // 1. Inspect ins elements for fixed pixel styles or attributes
    for (const ins of insElements) {
      const styleW = ins.style.width;
      const styleH = ins.style.height;
      if (styleW && styleW.includes('px')) {
        const parsedW = parseFloat(styleW);
        if (parsedW > targetWidth) targetWidth = parsedW;
      }
      if (styleH && styleH.includes('px')) {
        const parsedH = parseFloat(styleH);
        if (parsedH > targetHeight) targetHeight = parsedH;
      }
      const attrW = parseFloat(ins.getAttribute('data-ad-width') || '0');
      const attrH = parseFloat(ins.getAttribute('data-ad-height') || '0');
      if (attrW > targetWidth) targetWidth = attrW;
      if (attrH > targetHeight) targetHeight = attrH;
    }

    // 2. Inspect iframes (e.g. AdSense Google iframe or custom iframe ads)
    const iframes = Array.from(container.querySelectorAll('iframe')) as HTMLIFrameElement[];
    for (const iframe of iframes) {
      const attrW = parseFloat(iframe.getAttribute('width') || '0');
      const attrH = parseFloat(iframe.getAttribute('height') || '0');
      if (attrW > targetWidth) targetWidth = attrW;
      if (attrH > targetHeight) targetHeight = attrH;

      const styleW = iframe.style.width;
      const styleH = iframe.style.height;
      if (styleW && styleW.includes('px')) {
        const parsedW = parseFloat(styleW);
        if (parsedW > targetWidth) targetWidth = parsedW;
      }
      if (styleH && styleH.includes('px')) {
        const parsedH = parseFloat(styleH);
        if (parsedH > targetHeight) targetHeight = parsedH;
      }

      // Also check rendered width if greater
      if (iframe.offsetWidth > targetWidth) targetWidth = iframe.offsetWidth;
      if (iframe.offsetHeight > targetHeight) targetHeight = iframe.offsetHeight;
    }

    // 3. Inspect images or other elements with fixed widths
    const images = Array.from(container.querySelectorAll('img')) as HTMLImageElement[];
    for (const img of images) {
      const naturalW = img.naturalWidth || parseFloat(img.getAttribute('width') || '0');
      const naturalH = img.naturalHeight || parseFloat(img.getAttribute('height') || '0');
      if (naturalW > targetWidth) targetWidth = naturalW;
      if (naturalH > targetHeight) targetHeight = naturalH;
    }

    // 4. If targetWidth wasn't explicitly found, scan direct elements
    if (targetWidth === 0 && !isExplicitlyResponsive) {
      const allElements = Array.from(container.querySelectorAll('div, table, section')) as HTMLElement[];
      for (const el of allElements) {
        const sw = el.scrollWidth;
        const ow = el.offsetWidth;
        const w = Math.max(sw, ow);
        if (w > targetWidth && w > parentWidth) {
          targetWidth = w;
          targetHeight = Math.max(targetHeight, el.scrollHeight, el.offsetHeight);
        }
      }
    }

    // Default fallback height if only width was specified
    if (targetHeight === 0) {
      targetHeight = container.scrollHeight || 90;
    }

    // Apply proportional responsive scaling if the target width exceeds parent width
    if (targetWidth > 0 && targetWidth > parentWidth && !isExplicitlyResponsive) {
      const scale = Math.min(1, (parentWidth / targetWidth));
      const scaledHeight = Math.ceil(targetHeight * scale);

      if (wrapper) {
        wrapper.style.width = `${targetWidth}px`;
        wrapper.style.minWidth = `${targetWidth}px`;
        wrapper.style.maxWidth = `${targetWidth}px`;
        wrapper.style.transform = `scale(${scale})`;
        wrapper.style.transformOrigin = 'top center';
        wrapper.style.marginLeft = 'auto';
        wrapper.style.marginRight = 'auto';
        wrapper.style.display = 'block';
        wrapper.style.flexShrink = '0';
      }

      container.style.height = `${scaledHeight}px`;
      container.style.minHeight = `${scaledHeight}px`;
      container.style.overflow = 'hidden';
      container.style.display = 'flex';
      container.style.justifyContent = 'center';
      container.style.alignItems = 'flex-start';
      container.style.width = '100%';
    } else {
      // Fluid or fits naturally: allow native full-width rendering without cropping
      if (wrapper) {
        wrapper.style.width = '100%';
        wrapper.style.minWidth = '0';
        wrapper.style.maxWidth = '100%';
        wrapper.style.transform = 'none';
        wrapper.style.transformOrigin = 'center center';
        wrapper.style.margin = '0 auto';
        wrapper.style.display = 'block';
      }
      container.style.height = 'auto';
      container.style.minHeight = '0';
      container.style.overflow = 'visible';
      container.style.display = 'block';
      container.style.width = '100%';
    }
  }, []);

  useEffect(() => {
    if (placementConfig?.source !== 'network' || !placementConfig?.enabled || user?.planTier === 'paid' || !htmlContainerRef.current) return;

    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;
    let pushTimeout: any = null;
    let autoScaleIntervals: any[] = [];

    const executeSafePush = () => {
      if (!htmlContainerRef.current) return;
      const insElements = htmlContainerRef.current.querySelectorAll('ins.adsbygoogle');
      if (insElements.length === 0) return;

      try {
        // @ts-ignore
        const adsbygoogle = window.adsbygoogle || [];
        Array.from(insElements).forEach((ins: Element) => {
          const status = ins.getAttribute('data-adsbygoogle-status');
          const locallyPushed = ins.getAttribute('data-locally-pushed');
          if (!status && locallyPushed !== 'true') {
            ins.setAttribute('data-locally-pushed', 'true');
            adsbygoogle.push({});
            isPushedRef.current = true;
          }
        });
      } catch (e) {
        console.error(`[AdUnit:${placement}] Push failure:`, e);
      }
    };

    const initializeContent = () => {
      if (!htmlContainerRef.current || !scalingWrapperRef.current) return;
      isPushedRef.current = false;

      if (placementConfig.htmlSnippet) {
        scalingWrapperRef.current.innerHTML = placementConfig.htmlSnippet;

        // Execute any inline scripts correctly
        const scripts = Array.from(scalingWrapperRef.current.querySelectorAll('script'));
        for (const oldScript of scripts) {
          const scriptEl = oldScript as HTMLScriptElement;
          const newScript = document.createElement('script');
          Array.from(scriptEl.attributes).forEach((attr: Attr) => newScript.setAttribute(attr.name, attr.value));
          if (scriptEl.innerHTML) {
            newScript.appendChild(document.createTextNode(scriptEl.innerHTML));
            if (scriptEl.innerHTML.includes('adsbygoogle.push')) isPushedRef.current = true;
          }
          scriptEl.parentNode?.replaceChild(newScript, scriptEl);
        }
        
        // Multiple timed scaling passes to adjust cleanly as assets / iframes load
        const delays = [30, 100, 300, 700, 1500, 3000];
        delays.forEach(delay => {
          const t = setTimeout(executeSafeScale, delay);
          autoScaleIntervals.push(t);
        });
      }

      // ResizeObserver to detect container size changes (mobile rotation, viewport resize, sidebars)
      resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.contentRect.width > 0) {
            executeSafeScale();
            if (pushTimeout) clearTimeout(pushTimeout);
            pushTimeout = setTimeout(() => {
              executeSafePush();
              executeSafeScale();
            }, 200);
          }
        }
      });
      resizeObserver.observe(htmlContainerRef.current);

      // MutationObserver to detect when AdSense injects iframe or alters DOM
      mutationObserver = new MutationObserver(() => {
        executeSafeScale();
      });
      mutationObserver.observe(scalingWrapperRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'width', 'height', 'data-adsbygoogle-status']
      });

      window.addEventListener('resize', executeSafeScale);
      window.addEventListener('orientationchange', executeSafeScale);
    };

    initializeContent();
    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      if (mutationObserver) mutationObserver.disconnect();
      if (pushTimeout) clearTimeout(pushTimeout);
      autoScaleIntervals.forEach(clearTimeout);
      window.removeEventListener('resize', executeSafeScale);
      window.removeEventListener('orientationchange', executeSafeScale);
    };
  }, [placement, placementConfig, user?.planTier, uniqueId, executeSafeScale]);

  if (user?.planTier === 'paid') return null;
  if (!config.isActive || !placementConfig || !placementConfig.enabled) return null;

  const handleUpgradeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(`/${activeCert?.id || user?.activeCertId || 'cna'}/elite`);
  };

  // --- INTERNAL BESPOKE ADVERT RENDERER ---
  const hasNetworkSnippet = isNetworkSource && Boolean(placementConfig?.htmlSnippet?.trim());

  if (!hasNetworkSnippet) {
    const isStickyPlayer = normalizedPlacement === 'podcasts_player_sticky';
    if (isStickyPlayer) {
      return (
        <div id={uniqueId} className={`w-full max-w-full mb-1 animate-fade-in group no-print overflow-hidden ${className}`}>
          <a
            href={config.targetUrl || 'https://www.diamondnursingschool.com'}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between gap-3 px-3 py-1.5 bg-slate-900/90 hover:bg-slate-900 text-white rounded-xl border border-white/10 shadow-md backdrop-blur-md transition-all text-xs"
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="px-1.5 py-0.5 bg-indigo-500/30 text-indigo-300 rounded text-[7px] font-black uppercase tracking-wider shrink-0">Partner</span>
              <span className="font-bold text-[11px] truncate text-slate-200">{config.title || "Elite NCLEX & CNA Board Prep"}</span>
            </div>
            <span className="text-[9px] font-black text-indigo-400 uppercase tracking-wider shrink-0 flex items-center gap-1">
              {config.ctaText || 'Learn More'} <ChevronRight size={12} />
            </span>
          </a>
        </div>
      );
    }

    const isSidebar = normalizedPlacement === 'sidebar' || normalizedPlacement.includes('sidebar');
    const isHorizontal = !isSidebar && normalizedPlacement !== 'profile' && normalizedPlacement !== 'dashboard_welcome_right';

    return (
      <div id={uniqueId} className={`w-full max-w-full mb-6 animate-fade-in group no-print overflow-hidden ${className}`}>
        <div className="flex justify-between items-center px-1 mb-2">
            <span className="text-[8px] font-black text-slate-400 dark:text-slate-500 tracking-widest uppercase">Sponsored</span>
            {config.showUpgradeHint && (
                <button 
                  onClick={handleUpgradeClick} 
                  className="flex items-center gap-1.5 text-[8px] sm:text-[9px] font-black tracking-widest text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300 transition-all uppercase min-h-[32px] px-1"
                  aria-label="Remove advertisements with Elite Pro"
                >
                    <ShieldAlert size={11} className="shrink-0" /> Remove Ads
                </button>
            )}
        </div>

        {isAdmin && isNetworkSource && !placementConfig?.htmlSnippet?.trim() && (
            <div className="mx-0 mb-2 px-3 py-1.5 bg-amber-600 text-white rounded-lg flex items-center justify-between shadow-sm animate-pop text-[8px] font-black tracking-widest uppercase">
                <div className="flex items-center gap-2">
                    <ShieldCheck size={12} className="text-amber-200 shrink-0" />
                    <span className="truncate">Slot: {normalizedPlacement} (Internal Fallback — No AdSense Snippet)</span>
                </div>
            </div>
        )}

        <a 
          href={config.targetUrl || 'https://www.diamondnursingschool.com'} 
          target="_blank" 
          rel="noopener noreferrer"
          className={`block bg-white dark:bg-slate-900 rounded-2xl md:rounded-[2rem] overflow-hidden border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-xl hover:border-indigo-300 dark:hover:border-indigo-700 transition-all group/card ${isHorizontal ? 'flex flex-col sm:flex-row' : 'flex flex-col'}`}
        >
          <div className={`${isHorizontal ? 'w-full sm:w-2/5 md:w-1/3' : 'w-full'} aspect-video sm:aspect-auto sm:min-h-[140px] relative overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0`}>
            {config.mediaUrl ? (
                <img 
                  src={config.mediaUrl} 
                  className="w-full h-full object-cover transition-transform duration-[8s] group-hover/card:scale-105" 
                  alt={config.title || "Special Offer"} 
                  loading="lazy"
                />
            ) : (
                <div className="w-full h-full min-h-[120px] flex items-center justify-center text-slate-300 dark:text-slate-600"><ImageIcon size={36}/></div>
            )}
            <div className="absolute top-2 left-2 px-2 py-0.5 bg-slate-900/70 backdrop-blur-md rounded-md text-[7px] font-black text-white tracking-widest border border-white/10">Partner</div>
          </div>
          
          <div className="p-4 sm:p-5 md:p-6 flex flex-col justify-center flex-1 min-w-0">
            <h4 className="text-xs sm:text-sm md:text-base font-black text-slate-900 dark:text-white tracking-tight leading-snug mb-1 sm:mb-1.5 group-hover/card:text-indigo-600 dark:group-hover/card:text-indigo-400 transition-colors break-words">
              {config.title || "Elite NCLEX & CNA Board Mastery Prep"}
            </h4>
            <p className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 font-medium leading-relaxed mb-3 break-words">
              "{config.description || "Unlock 1,500+ authentic state board practice items, procedural clinical simulations, and verified instructor rationales."}"
            </p>
            <div className="flex items-center gap-1.5 text-[10px] font-black text-indigo-600 dark:text-indigo-400 tracking-widest uppercase mt-auto pt-1">
              <span>{config.ctaText || 'Learn More'}</span>
              <ChevronRight size={14} className="group-hover/card:translate-x-1 transition-transform shrink-0" />
            </div>
          </div>
        </a>
      </div>
    );
  }

  // --- NETWORK AD RENDERER ---
  return (
    <div id={uniqueId} className={`w-full max-w-full mb-6 animate-fade-in group no-print overflow-hidden ${className}`}>
        <div className="flex justify-between items-center px-1 mb-2">
            <span className="text-[8px] font-black text-slate-400 tracking-widest uppercase">Sponsored</span>
            {config.showUpgradeHint && (
                <button 
                  onClick={handleUpgradeClick} 
                  className="flex items-center gap-1.5 text-[8px] sm:text-[9px] font-black tracking-widest text-indigo-400 hover:text-indigo-600 transition-all uppercase min-h-[32px] px-1"
                  aria-label="Remove advertisements with Elite Pro"
                >
                    <ShieldAlert size={11} className="shrink-0" /> Remove Ads
                </button>
            )}
        </div>
        
        {isAdmin && (
            <div className="mx-0 mb-2 px-3 py-1.5 bg-indigo-600 text-white rounded-lg flex items-center justify-between shadow-lg animate-pop">
                <div className="flex items-center gap-2 min-w-0">
                    <ShieldCheck size={12} className="text-indigo-200 shrink-0" />
                    <span className="text-[8px] font-black tracking-widest uppercase truncate">Slot: {normalizedPlacement}</span>
                </div>
            </div>
        )}

        <div className="w-full bg-transparent overflow-hidden relative p-0 m-0 flex justify-center items-center">
            {!isReady && (
              <div className="absolute inset-0 flex items-center justify-center py-4">
                <PageLoader compact size="sm" />
              </div>
            )}
            
            <style>{`
                #${uniqueId} {
                    width: 100% !important;
                    max-width: 100% !important;
                    overflow: hidden !important;
                    box-sizing: border-box !important;
                }
                #${uniqueId} .ad-container-outer {
                    width: 100% !important;
                    max-width: 100% !important;
                    text-align: center !important;
                    margin: 0 auto !important;
                    padding: 0 !important;
                    box-sizing: border-box !important;
                    overflow: hidden !important;
                }
                #${uniqueId} .ad-scaling-inner {
                    display: block !important;
                    margin: 0 auto !important;
                    text-align: center !important;
                    box-sizing: border-box !important;
                }
                #${uniqueId} .ad-scaling-inner ins.adsbygoogle {
                    display: block !important;
                    margin: 0 auto !important;
                }
                #${uniqueId} .ad-scaling-inner iframe {
                    margin: 0 auto !important;
                    display: block !important;
                    border: none !important;
                }
                #${uniqueId} .ad-scaling-inner img {
                    display: inline-block !important;
                    max-width: 100% !important;
                    height: auto !important;
                }
            `}</style>
            
            {placementConfig.htmlSnippet ? (
              <div 
                ref={htmlContainerRef} 
                className="ad-container-outer no-scrollbar"
                style={{ display: 'block', width: '100%', maxWidth: '100%', minWidth: 0 }}
              >
                <div 
                  ref={scalingWrapperRef}
                  className="ad-scaling-inner"
                  style={{ width: '100%', maxWidth: '100%' }}
                />
              </div>
            ) : (
              <div className="p-8 text-center space-y-3 opacity-40 grayscale flex flex-col items-center">
                {isAdmin ? (
                   <>
                    <Megaphone size={32} className="text-indigo-400" />
                    <p className="text-[9px] font-black tracking-widest leading-tight">No AdSense Snippet provisioned<br/>for this placement key.</p>
                   </>
                ) : (
                  <PageLoader compact size="sm" />
                )}
              </div>
            )}
        </div>
    </div>
  );
};
